import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { serviceCategoriesService, servicesService } from '../services/api';

interface ServiceFormData {
  title: string;
  description: string;
  category: string;
  location: string;
  budget: number;
  currency: string;
  deadline: string;
  is_urgent: boolean;
  requirements: string[];
}

const fallbackServiceCategories = [
  { value: 'cleaning', label: 'Cleaning Services' },
  { value: 'repair', label: 'Repair & Maintenance' },
  { value: 'delivery', label: 'Delivery Services' },
  { value: 'tutoring', label: 'Tutoring & Education' },
  { value: 'design', label: 'Design & Creative' },
  { value: 'writing', label: 'Writing & Content' },
  { value: 'programming', label: 'Programming & Tech' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'fitness', label: 'Fitness & Health' },
  { value: 'beauty', label: 'Beauty & Wellness' },
  { value: 'photography', label: 'Photography & Video' },
  { value: 'translation', label: 'Translation & Languages' },
  { value: 'legal', label: 'Legal Services' },
  { value: 'accounting', label: 'Accounting & Finance' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'event-planning', label: 'Event Planning' },
  { value: 'catering', label: 'Catering & Food' },
  { value: 'landscaping', label: 'Landscaping & Gardening' },
  { value: 'moving', label: 'Moving & Transportation' },
  { value: 'house-hunting', label: 'House Hunting & Real Estate' },
  { value: 'other', label: 'Other Services' }
];

const ServiceRequestPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ServiceFormData>({
    title: '',
    description: '',
    category: '',
    location: '',
    budget: 0,
    currency: 'KSH',
    deadline: '',
    is_urgent: false,
    requirements: []
  });
  const [newRequirement, setNewRequirement] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceCategories, setServiceCategories] = useState(fallbackServiceCategories);

  useEffect(() => {
    const loadServiceCategories = async () => {
      try {
        const response = await serviceCategoriesService.getCategories(false);
        const categories = (response.data || []).map((category: { name: string, description: string }) => ({
          value: category.name,
          label: category.name
        }));

        if (categories.length > 0) {
          setServiceCategories(categories);
        }
      } catch (loadError) {
        console.error('Failed to load service categories:', loadError);
      }
    };

    loadServiceCategories();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim() && formData.requirements.indexOf(newRequirement.trim()) === -1) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await servicesService.createService(formData);
      navigate(`/services/${response.data.id}`);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to create service request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Request a Service</h1>
            <p className="text-gray-600 mb-8">
              Describe the service you need and service providers will bid on your request.
            </p>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="text-red-400">⚠️</div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Service Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., House Cleaning Service, Website Design, Tutoring..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a category</option>
                    {serviceCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Nairobi CBD, Westlands, Karen..."
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Provide detailed information about what you need, including specific requirements, timeline, and any other relevant details..."
                />
              </div>

              {/* Budget and Deadline */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Budget <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="border border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="KSH">KSH</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                    <input
                      type="number"
                      name="budget"
                      value={formData.budget}
                      onChange={handleNumberChange}
                      min="0"
                      step="0.01"
                      required
                      className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 border-l-0"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Your maximum budget for this service
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Deadline (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    When do you need this service completed?
                  </p>
                </div>
              </div>

              {/* Requirements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specific Requirements
                </label>
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add a specific requirement..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                    />
                    <button
                      type="button"
                      onClick={addRequirement}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                    >
                      Add
                    </button>
                  </div>

                  {formData.requirements.length > 0 && (
                    <div className="space-y-2">
                      {formData.requirements.map((requirement, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                          <span className="text-sm text-gray-700">{requirement}</span>
                          <button
                            type="button"
                            onClick={() => removeRequirement(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Urgent Flag */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_urgent"
                    checked={formData.is_urgent}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Mark as urgent 🔥 (higher visibility to providers)
                  </span>
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/services')}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Creating...' : 'Create Service Request'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-3">💡 Tips for a Great Service Request</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Be specific about what you need - the more details, the better the bids</li>
            <li>• Include your budget range to attract suitable providers</li>
            <li>• Set a realistic deadline for quality work</li>
            <li>• List any special requirements or preferences</li>
            <li>• Use the urgent flag only when truly necessary</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestPage;